import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API, LOGO_URL } from "@/lib/api";
import VisualShowcase from "@/components/VisualShowcase";
import TestimonialMarquee from "@/components/TestimonialMarquee";
import axios from "axios";
import {
  Sparkle, VideoCamera, Microphone, Lightning,
  ArrowRight, EnvelopeSimple, Lock, Eye, EyeSlash, UserPlus, Play,
  Briefcase, Chalkboard, MicrophoneStage, Storefront, GraduationCap, Star,
  ChatCircleDots, DiscordLogo, Check,
} from "@phosphor-icons/react";

// Outcome-based use cases (replaces the "creators only" framing).
const USE_CASES = [
  { icon: Storefront, label: "Entrepreneurs", desc: "Show up for your business without filming yourself daily." },
  { icon: Chalkboard, label: "Coaches & Consultants", desc: "Turn frameworks into short-form video in minutes." },
  { icon: MicrophoneStage, label: "Speakers & Experts", desc: "Build thought leadership without a production team." },
  { icon: Briefcase, label: "Professionals", desc: "Realtors, attorneys, and service providers who need to stay visible." },
  { icon: GraduationCap, label: "Educators & Course Creators", desc: "Ship lessons, tutorials, and promos on autopilot." },
  { icon: VideoCamera, label: "Content Creators", desc: "Batch a month of content in one focused session." },
];

// What users get — outcome-framed, no provider names.
const OUTCOMES = [
  { emoji: "🎥", title: "Create educational videos", desc: "Explain your expertise in clean, captioned reels — no camera required." },
  { emoji: "📣", title: "Promote your offers", desc: "Turn one offer into a week of teaser videos, hooks, and stories." },
  { emoji: "🧠", title: "Build thought leadership", desc: "LinkedIn-ready long-form scripts and talking-head clips from a single idea." },
  { emoji: "🔥", title: "Stay visible without filming", desc: "Batch a month of content in one afternoon — your AI Twin does the on-camera work." },
];

// Lightweight FAQs (no BYOK / provider names / cost calculator).
const FAQS = [
  {
    q: "What's included in the 50 free credits?",
    a: "Enough to generate roughly 5-8 short-form talking-head videos, a stack of scripts, and dozens of AI-Twin images. No credit card needed to start.",
  },
  {
    q: "Do I need to connect any other tools or subscriptions?",
    a: "No. Everything runs inside The Branding Bar Lab. When you upgrade to a paid plan, the platform covers the AI providers behind the scenes.",
  },
  {
    q: "What's an AI Twin?",
    a: "You upload one selfie and the platform generates a photo-real digital version of you that can be placed in any scene, styled in any outfit, and lip-synced to any script.",
  },
  {
    q: "Can I use my own voice?",
    a: "Yes — Clone My Voice lets you record 30 seconds and your videos will speak in your actual voice. Or use one of the built-in high-quality voices.",
  },
  {
    q: "What happens after my free credits?",
    a: "Pick from Starter, Creator, Pro, or Studio plans — starting at $39/mo. Downgrade or cancel anytime. See the Plans page for exact volumes.",
  },
];

function formatApiError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const LoginPage = () => {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const [authMode, setAuthMode] = useState(null); // null, "login", "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [heroVideo, setHeroVideo] = useState(null); // { url, poster } — pulled from admin/showcase
  const [openFaq, setOpenFaq] = useState(0);
  const heroVideoRef = useRef(null);

  // Grab the "finished reel" from the same showcase feed the demo strip uses
  // so the hero always features a real, current output.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/showcase`);
        const finished = (r.data?.steps || []).find((s) => s.n === 4 && s.type === "video");
        if (finished) setHeroVideo({ url: finished.url, poster: finished.poster });
      } catch { /* silent fallback — hero just shows the static block */ }
    })();
  }, []);

  const playHero = () => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = false;
    v.play().catch(() => {});
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const endpoint = authMode === "register" ? `${API}/auth/register` : `${API}/auth/login-email`;
      const body = authMode === "register"
        ? { email, password, name }
        : { email, password };
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(formatApiError(data.detail));
        return;
      }
      // Store session token and set user
      if (data.session_token) {
        localStorage.setItem("session_token", data.session_token);
      }
      setUser(data);
      navigate(data.onboarded ? "/dashboard" : "/onboarding", { replace: true });
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="login-page">
      {/* Header */}
      <header className="w-full flex items-center justify-between px-4 sm:px-10 py-3 sm:py-5 border-b border-zinc-100 sticky top-0 bg-white/90 backdrop-blur-md z-50">
        <img src={LOGO_URL} alt="The Branding Bar" className="h-8 sm:h-10 object-contain" />
        <div className="flex items-center gap-1 sm:gap-3">
          <button
            onClick={() => setAuthMode("login")}
            data-testid="sign-in-btn-header"
            className="px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-zinc-700 hover:text-[#E91E8C] transition-all"
          >
            Sign In
          </button>
          <button
            onClick={() => setAuthMode("register")}
            data-testid="get-started-btn-header"
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-[#E91E8C] text-white text-xs sm:text-sm font-semibold rounded-full hover:bg-[#d4177d] transition-all shadow-md hover:shadow-lg"
          >
            Get Started Free
          </button>
        </div>
      </header>

      {/* Auth Modal */}
      {authMode && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-end sm:items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) setAuthMode(null); }}>
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 sm:p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto" data-testid="auth-modal">
            <button onClick={() => { setAuthMode(null); setError(""); }} className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 text-xl">&times;</button>

            <div className="text-center mb-6">
              <img src={LOGO_URL} alt="The Branding Bar" className="h-8 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-zinc-900">
                {authMode === "register" ? "Create your account" : "Welcome back"}
              </h2>
              <p className="text-zinc-500 text-sm mt-1">
                {authMode === "register" ? "Start creating AI-powered content in seconds" : "Sign in to your dashboard"}
              </p>
            </div>

            {/* Google Auth */}
            <button
              onClick={handleGoogleLogin}
              data-testid="google-auth-btn"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-zinc-200 rounded-xl font-semibold text-zinc-800 hover:bg-zinc-50 hover:border-zinc-300 transition-all mb-4"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="h-px bg-zinc-200 flex-1" />
              <span className="text-xs text-zinc-400 font-medium">or</span>
              <div className="h-px bg-zinc-200 flex-1" />
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleEmailAuth} className="space-y-3">
              {authMode === "register" && (
                <div>
                  <label className="block text-xs text-zinc-500 font-medium mb-1">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    data-testid="auth-name-input"
                    className="w-full px-4 py-2.5 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 focus:border-[#E91E8C]"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs text-zinc-500 font-medium mb-1">Email</label>
                <div className="relative">
                  <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="auth-email-input"
                    className="w-full pl-10 pr-4 py-2.5 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 focus:border-[#E91E8C]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 font-medium mb-1">Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={authMode === "register" ? "Min 6 characters" : "Your password"}
                    required
                    minLength={authMode === "register" ? 6 : 1}
                    data-testid="auth-password-input"
                    className="w-full pl-10 pr-10 py-2.5 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 focus:border-[#E91E8C]"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-red-500 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="auth-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="w-full py-3 bg-[#E91E8C] text-white font-semibold rounded-xl hover:bg-[#d4177d] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : authMode === "register" ? (
                  <><UserPlus size={18} /> Create Account</>
                ) : (
                  <><Lock size={16} /> Sign In</>
                )}
              </button>
            </form>

            <p className="text-center text-xs text-zinc-500 mt-4">
              {authMode === "register" ? (
                <>Already have an account? <button onClick={() => { setAuthMode("login"); setError(""); }} className="text-[#E91E8C] font-medium hover:underline" data-testid="switch-to-login">Sign in</button></>
              ) : (
                <>Don&apos;t have an account? <button onClick={() => { setAuthMode("register"); setError(""); }} className="text-[#E91E8C] font-medium hover:underline" data-testid="switch-to-register">Create one</button></>
              )}
            </p>
          </div>
        </div>
      )}

      {/* ────────── HERO — outcome-focused with finished video ────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E91E8C]/[0.06] via-transparent to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 py-14 sm:py-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center relative">
          {/* Copy */}
          <div className="text-left">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#E91E8C] mb-3" data-testid="hero-eyebrow">
              Your AI-powered content team
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-zinc-900 leading-[1.05] tracking-tight mb-5" data-testid="hero-headline">
              Turn one idea into a{" "}
              <span className="text-[#E91E8C]">fully produced video</span>
              <span className="whitespace-nowrap"> — </span>
              without filming yourself.
            </h1>
            <p className="text-base sm:text-lg text-zinc-600 leading-relaxed mb-7 max-w-xl" data-testid="hero-sub">
              The Branding Bar Lab writes your script, clones your voice, creates your AI Twin video,
              lip-syncs it, and adds captions — all in one place.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-3">
              <button
                onClick={() => setAuthMode("register")}
                data-testid="hero-signup-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#E91E8C] text-white font-bold rounded-2xl hover:bg-[#d4177d] transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 text-base sm:text-lg"
              >
                Create My First Video Free
                <ArrowRight size={20} weight="bold" />
              </button>
              <button
                onClick={handleGoogleLogin}
                data-testid="hero-google-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-6 py-4 bg-white text-zinc-800 font-semibold rounded-2xl border-2 border-zinc-200 hover:border-zinc-300 transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>
            </div>
            <p className="text-sm text-zinc-500 flex items-center gap-1.5">
              <Sparkle size={14} weight="fill" className="text-[#E91E8C]" />
              50 free credits. No card required.
            </p>
            <p className="text-xs text-zinc-500 mt-3 max-w-lg">
              Built for entrepreneurs, coaches, consultants, creators, and professionals who need to show up consistently.
            </p>
          </div>

          {/* Hero visual — finished reel */}
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#E91E8C]/25 to-purple-400/25 rounded-[2rem] blur-2xl opacity-70" />
            <div className="relative aspect-[9/16] max-h-[520px] mx-auto rounded-[1.5rem] overflow-hidden bg-zinc-900 border-4 border-white shadow-2xl" data-testid="hero-video-container">
              {heroVideo ? (
                <>
                  <video
                    ref={heroVideoRef}
                    src={heroVideo.url}
                    poster={heroVideo.poster}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                    data-testid="hero-video"
                  />
                  <button
                    onClick={playHero}
                    className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-[#E91E8C] flex items-center justify-center shadow-lg backdrop-blur-md"
                    data-testid="hero-play-sound-btn"
                    title="Play with sound"
                  >
                    <Play size={18} weight="fill" />
                  </button>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/50 text-sm">
                  Finished reel preview
                </div>
              )}
              <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white text-[10px] font-semibold" data-testid="hero-video-badge">
                <Sparkle size={11} weight="fill" className="text-[#E91E8C]" />
                Created entirely inside The Branding Bar Lab
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────── TRUSTED / MINI PROOF ────────── */}
      <section className="border-y border-zinc-100 bg-zinc-50/60">
        <div className="max-w-5xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">
            Trusted by early Branding Bar creators &amp; students
          </p>
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><Check size={13} className="text-emerald-500" weight="bold" /> No filming required</span>
            <span className="flex items-center gap-1"><Check size={13} className="text-emerald-500" weight="bold" /> One dashboard</span>
            <span className="flex items-center gap-1"><Check size={13} className="text-emerald-500" weight="bold" /> Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* ────────── BEFORE-TO-AFTER 4-STEP DEMO (existing showcase) ────────── */}
      <VisualShowcase />

      {/* ────────── OUTCOMES — what the platform does FOR you ────────── */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <p className="text-center text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">What you&apos;ll actually get</p>
        <h2 className="text-center text-3xl sm:text-4xl font-bold text-zinc-900 mb-3 max-w-2xl mx-auto">
          A whole content team,{" "}
          <span className="text-[#E91E8C]">packed into one dashboard.</span>
        </h2>
        <p className="text-center text-zinc-500 max-w-xl mx-auto mb-12">
          Every tool below is included in every plan. You&apos;re only picking how much you want to ship each month.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {OUTCOMES.map((o) => (
            <div key={o.title} className="p-5 rounded-2xl border border-zinc-100 bg-white hover:border-[#E91E8C]/30 hover:shadow-sm transition-all">
              <div className="text-2xl mb-2">{o.emoji}</div>
              <h3 className="font-bold text-zinc-900 mb-1">{o.title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">{o.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ────────── USE CASES BY CUSTOMER TYPE ────────── */}
      <section className="bg-zinc-50/70 border-y border-zinc-100">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Who it&apos;s for</p>
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-zinc-900 mb-3">Not just for creators.</h2>
          <p className="text-center text-zinc-500 max-w-2xl mx-auto mb-10">
            If your business grows when you&apos;re visible, The Branding Bar Lab helps you show up consistently — without becoming a full-time content producer.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {USE_CASES.map((u) => (
              <div key={u.label} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-zinc-100">
                <div className="w-10 h-10 rounded-lg bg-[#E91E8C]/10 flex items-center justify-center flex-shrink-0">
                  <u.icon size={20} weight="duotone" className="text-[#E91E8C]" />
                </div>
                <div>
                  <p className="font-semibold text-zinc-900 text-sm">{u.label}</p>
                  <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{u.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────── SCROLLING TESTIMONIAL MARQUEE ────────── */}
      <TestimonialMarquee />

      {/* ────────── SIMPLE PRICING TEASER ────────── */}
      <section className="bg-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E91E8C]/15 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 py-14 relative">
          <p className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[#E91E8C] mb-2">Pricing you can read in 10 seconds</p>
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-white mb-2">Start free. Pay only when you&apos;re ready.</h2>
          <p className="text-center text-zinc-400 max-w-xl mx-auto mb-8">
            Every plan includes every tool. You&apos;re only picking your monthly video volume.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
            <PricingChip onClick={() => navigate("/plans")} name="Free" price="$0" videos="2 short videos" note="50 credits · no card" primary={false} />
            <PricingChip onClick={() => navigate("/plans")} name="Starter" price="$39/mo" videos="8 videos / mo" note="Great to get consistent" primary={false} />
            <PricingChip onClick={() => navigate("/plans")} name="Creator" price="$79/mo" videos="20 videos / mo" note="Most popular" primary={true} />
            <PricingChip onClick={() => navigate("/plans")} name="Pro" price="$159/mo" videos="50 videos / mo" note="Best value for daily posters" primary={false} />
          </div>
          <div className="text-center">
            <button
              onClick={() => navigate("/plans")}
              className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[#E91E8C]"
              data-testid="landing-see-plans-btn"
            >
              See full plan comparison <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* ────────── FAQ (light — no BYOK / cost calculator / provider names) ────────── */}
      <section className="max-w-3xl mx-auto px-4 py-16" id="faq" data-testid="landing-faq">
        <p className="text-center text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Frequently asked</p>
        <h2 className="text-center text-2xl sm:text-3xl font-bold text-zinc-900 mb-8">Questions before you sign up?</h2>
        <div className="space-y-2">
          {FAQS.map((f, i) => (
            <div key={i} className={`rounded-xl border ${openFaq === i ? "border-[#E91E8C]/40 bg-[#E91E8C]/[0.03]" : "border-zinc-200 bg-white"}`}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                data-testid={`faq-q-${i}`}
              >
                <span className="font-semibold text-zinc-900 text-sm">{f.q}</span>
                <span className={`text-[#E91E8C] text-lg font-bold transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {openFaq === i && (
                <p className="px-4 pb-4 text-sm text-zinc-600 leading-relaxed" data-testid={`faq-a-${i}`}>{f.a}</p>
              )}
            </div>
          ))}
        </div>

        {/* Backup: chat, Discord, email */}
        <div className="mt-8 p-5 rounded-2xl bg-zinc-50 border border-zinc-100">
          <p className="text-sm font-semibold text-zinc-900 mb-3 flex items-center gap-2">
            <ChatCircleDots size={16} weight="fill" className="text-[#E91E8C]" />
            Still stuck? We&apos;re around.
          </p>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <a
              href="https://discord.gg/BmwfRzykqX"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 font-semibold text-[#5865F2] hover:underline"
              data-testid="landing-discord-link"
            >
              <DiscordLogo size={16} weight="fill" /> Join our Discord community
            </a>
            <span className="text-zinc-300">·</span>
            <a
              href="mailto:hello@thebrandingbarlab.com"
              className="inline-flex items-center gap-1.5 font-semibold text-zinc-700 hover:text-[#E91E8C]"
              data-testid="landing-email-link"
            >
              <EnvelopeSimple size={16} weight="duotone" /> hello@thebrandingbarlab.com
            </a>
          </div>
        </div>
      </section>

      {/* ────────── FINAL CTA ────────── */}
      <section className="bg-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#E91E8C]/20 to-transparent pointer-events-none" />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center relative">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Ready to show up consistently?</h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-lg mx-auto">
            Sign up in 30 seconds. Ship your first fully-produced video today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 px-4 sm:px-0">
            <button
              onClick={() => setAuthMode("register")}
              data-testid="cta-signup-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#E91E8C] text-white font-bold rounded-2xl hover:bg-[#d4177d] transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 text-base sm:text-lg"
            >
              Create My First Video Free <ArrowRight size={20} weight="bold" />
            </button>
            <button
              onClick={() => setAuthMode("login")}
              data-testid="cta-login-btn"
              className="inline-flex items-center gap-2 px-6 py-3 text-zinc-400 hover:text-white transition-all text-sm font-medium"
            >
              Already have an account? Sign in
            </button>
          </div>
          <p className="text-zinc-500 text-sm mt-4">50 free credits included. No card required.</p>
        </div>
      </section>

      {/* ────────── FOOTER ────────── */}
      <footer className="text-center py-6 border-t border-zinc-100">
        <p className="text-zinc-400 text-xs">
          &copy; The Branding Bar Lab &middot; Built by{" "}
          <a href="https://thebrandingbar.com" target="_blank" rel="noreferrer" className="hover:text-[#E91E8C]">Brittany Benson</a>
          {" · "}
          <a href="mailto:hello@thebrandingbarlab.com" className="hover:text-[#E91E8C]">Support</a>
          {" · "}
          <a href="https://discord.gg/BmwfRzykqX" target="_blank" rel="noreferrer" className="hover:text-[#E91E8C]">Discord</a>
        </p>
      </footer>
    </div>
  );
};

const PricingChip = ({ name, price, videos, note, primary, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`landing-pricing-chip-${name.toLowerCase()}`}
    className={`p-4 rounded-2xl text-center border-2 transition-all hover:-translate-y-0.5 hover:shadow-xl cursor-pointer ${primary ? "border-[#E91E8C] bg-[#E91E8C]/10 hover:bg-[#E91E8C]/15" : "border-zinc-800 bg-zinc-800/50 hover:border-zinc-600"}`}
  >
    <p className={`text-xs font-bold uppercase tracking-wider ${primary ? "text-[#E91E8C]" : "text-zinc-400"}`}>{name}</p>
    <p className="text-2xl font-extrabold text-white mt-1">{price}</p>
    <p className="text-xs text-zinc-300 font-semibold mt-1">{videos}</p>
    <p className="text-[10px] text-zinc-500 mt-1">{note}</p>
  </button>
);

export default LoginPage;
